import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Copy, Merge, X, AlertCircle, CheckCircle2, Loader2, ChevronDown, ChevronRight } from "lucide-react";

/**
 * DedupePanel — flags potential duplicate certificate records and lets the
 * admin merge (keep one, fill its empty fields from the rest) or dismiss.
 */
export default function DedupePanel({ onChange }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [err, setErr] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  const [keepByGroup, setKeepByGroup] = useState({}); // {groupKey: keep_id}

  const refresh = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get("/certificates/dedupe/candidates");
      setGroups(r.data.groups || []);
      // default keeper = richest row (most non-empty fields)
      const defaults = {};
      (r.data.groups || []).forEach((g) => {
        const ranked = [...g.rows].sort((a, b) => scoreRow(b) - scoreRow(a));
        defaults[g.key] = ranked[0]?.id;
      });
      setKeepByGroup(defaults);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) refresh(); }, [open]); // eslint-disable-line

  const merge = async (g) => {
    const keep_id = keepByGroup[g.key];
    if (!keep_id) return;
    const discard_ids = g.rows.map((r) => r.id).filter((id) => id !== keep_id);
    if (!discard_ids.length) return;
    if (!window.confirm(`Keep 1 row and delete ${discard_ids.length} other${discard_ids.length === 1 ? "" : "s"} after merging their non-empty fields into the keeper?`)) return;
    setBusyKey(g.key); setErr("");
    try {
      await api.post("/certificates/dedupe/merge", { keep_id, discard_ids });
      await refresh();
      onChange && onChange();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyKey(null); }
  };

  const dismiss = async (g) => {
    setBusyKey(g.key); setErr("");
    try {
      await api.post("/certificates/dedupe/dismiss", { key: g.key });
      setGroups((prev) => prev.filter((x) => x.key !== g.key));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusyKey(null); }
  };

  return (
    <section className="mt-4 border border-slate-200 rounded-md bg-white" data-testid="certificates-dedupe-panel">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="dedupe-toggle"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
          <Copy className="h-4 w-4 text-nkf-red" />
          <span className="font-display text-nkf-blue">Possible Duplicates</span>
          {groups.length > 0 && (
            <span className="inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full text-[11px] font-bold bg-nkf-red text-white">
              {groups.length}
            </span>
          )}
        </div>
        <span className="text-[11px] uppercase tracking-widest text-slate-500">
          {loading ? "Scanning…" : open ? "Hide" : "Review"}
        </span>
      </button>

      {open && (
        <div className="border-t border-slate-200 p-4 space-y-3">
          {err && <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded text-sm"><AlertCircle className="h-4 w-4 mt-0.5" /> {err}</div>}
          {!loading && groups.length === 0 && (
            <div className="text-center text-slate-500 text-sm py-6 border border-dashed rounded">
              <CheckCircle2 className="h-6 w-6 mx-auto text-emerald-500 mb-2" />
              No duplicates found. Nice and clean.
            </div>
          )}
          {loading && <div className="text-center text-slate-500 text-sm py-6"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Scanning registry for duplicates…</div>}
          {groups.map((g) => (
            <div key={g.key} className="border border-slate-200 rounded-md overflow-hidden" data-testid={`dedupe-group-${g.key}`}>
              <div className="bg-slate-50 px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-nkf-red font-semibold">
                    {g.signal === "cert_number" ? "Same certificate number" : "Same name · belt · year"}
                  </div>
                  <div className="font-display text-sm text-nkf-blue truncate">{g.label}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => dismiss(g)}
                    disabled={busyKey === g.key}
                    data-testid={`dedupe-dismiss-${g.key}`}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                    title="Not duplicates — hide forever"
                  >
                    <X className="h-3.5 w-3.5" /> Not Duplicates
                  </button>
                  <button
                    onClick={() => merge(g)}
                    disabled={busyKey === g.key || !keepByGroup[g.key]}
                    data-testid={`dedupe-merge-${g.key}`}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded bg-nkf-blue text-white hover:bg-nkf-navy disabled:opacity-50"
                  >
                    <Merge className="h-3.5 w-3.5" /> {busyKey === g.key ? "Merging…" : `Merge (keep 1 of ${g.rows.length})`}
                  </button>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-widest text-slate-500">
                  <tr className="border-t border-slate-100">
                    <th className="text-center px-3 py-1.5 w-12">Keep</th>
                    <th className="text-left px-3 py-1.5">Name</th>
                    <th className="text-left px-3 py-1.5">Belt</th>
                    <th className="text-left px-3 py-1.5">Year</th>
                    <th className="text-left px-3 py-1.5">Academy / District</th>
                    <th className="text-left px-3 py-1.5">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {g.rows.map((r) => {
                    const chosen = keepByGroup[g.key] === r.id;
                    return (
                      <tr key={r.id} className={chosen ? "bg-emerald-50/50" : ""} data-testid={`dedupe-row-${r.id}`}>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="radio"
                            name={`keep-${g.key}`}
                            checked={chosen}
                            onChange={() => setKeepByGroup((k) => ({ ...k, [g.key]: r.id }))}
                            data-testid={`dedupe-keep-${r.id}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-slate-800">{r.athlete_name || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{r.belt_rank || "—"}</td>
                        <td className="px-3 py-2 text-slate-600">{r.exam_year || "—"}</td>
                        <td className="px-3 py-2 text-slate-600 text-xs truncate max-w-[260px]">{[r.academy, r.district, r.state].filter(Boolean).join(" · ") || "—"}</td>
                        <td className="px-3 py-2 text-slate-500 text-xs truncate max-w-[180px]">{r.source_file || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Rank a row by how many non-empty fields it has — the "richest" row is a
// good default keeper during merge.
function scoreRow(r) {
  const fields = ["certificate_number", "belt_rank", "belt_color", "kyu_or_dan",
    "exam_date", "exam_year", "style", "format", "coach_name", "dojo",
    "academy", "district", "state", "class_or_school"];
  return fields.reduce((n, f) => n + (r[f] ? 1 : 0), 0);
}
